import { format, parseISO } from 'date-fns';
import { XCircle } from 'lucide-react';
import { fmtCurrency } from '@/utils/money';

/**
 * One payment attempt that did not go through, in the Payment History
 * timeline beside the payments that did (see server/payment-attempts.js).
 * Admins only: customers never see these rows.
 *
 * Shows the time, not just the day: retries and a customer's repeated tries
 * often land on the same date, and the order is the story. Never counted in
 * any paid total — the amount is labelled "Not charged".
 *
 * `showRaw` (staff only) adds the processor's own response under the
 * plain-English reason, so an admin can tell a dead card from a short month.
 */
export default function FailedAttemptPill({ attempt, showRaw = false }) {
  const at = attempt?.at ? parseISO(attempt.at) : null;
  const when = at && !Number.isNaN(at.getTime()) ? format(at, 'EEE, MMM d, yyyy · h:mm a') : '—';
  const card = attempt?.card_last4
    ? `${attempt.card_brand ? `${attempt.card_brand} ` : ''}••${attempt.card_last4}`
    : null;
  return (
    <div
      className="flex items-center justify-between gap-3 py-2.5 px-4 rounded-2xl bg-rose-950/40 border border-rose-800/40"
      data-testid="failed-attempt"
    >
      <div className="flex items-center gap-3 min-w-0">
        <XCircle className="w-4 h-4 flex-shrink-0 text-rose-300" aria-hidden="true" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-white/90">{when}</div>
          <div className="text-[11px] text-rose-100/75 leading-snug">
            {attempt?.kind_label || 'Payment'} declined · {attempt?.reason || "Declined by the card's bank"}
            {card ? ` · ${card}` : ''}
          </div>
          {showRaw && attempt?.raw_reason ? (
            <div className="text-[11px] text-white/45 leading-snug" title="Processor response (staff only)">
              {attempt.raw_reason}
            </div>
          ) : null}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-medium text-white/70 tabular-nums">
          ${fmtCurrency(attempt?.amount_cents || 0)}
        </div>
        <div className="text-[11px] text-rose-200/70">Not charged</div>
      </div>
    </div>
  );
}
